import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  Search,
  Heart,
  TrendingUp,
} from "lucide-react";

const MOCKUP =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/0fe3xprx_kickz%20app%20mockup.jpg";
const GRADIENT_LOGO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/sfedu680_image.png";
const LOGO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/98icay2r_Asset%2010.png";
const VIDEO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/lm0xelkx_sneaker.mp4";
const FEED_SCREEN =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/3ubf4z34_image.png";
const COLLECTION_SCREEN =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/dbonxbp8_image.png";

const palette = [
  { name: "Hype Orange", hex: "#E8721C", text: "#fff" },
  { name: "Court Blue", hex: "#35B6E9", text: "#fff" },
  { name: "Ink Black", hex: "#111111", text: "#fff" },
  { name: "Box Fresh", hex: "#F7F2EA", text: "#111" },
];

const features = [
  {
    icon: Search,
    title: "Browse & cop",
    body: "A fast home feed with search, featured drops and color-blocked product tiles — orange and blue cards make every sneaker pop off the shelf.",
    bg: "#35B6E9",
  },
  {
    icon: Bell,
    title: "Drop alerts",
    body: "The Drops tab lists every upcoming release with date and time. One flick of the toggle and the app pings you the second a pair goes live.",
    bg: "#E8721C",
  },
  {
    icon: Heart,
    title: "Wishlist",
    body: "Heart any pair to build a personal rotation. Wishlisted sneakers surface first when restocks and price drops hit.",
    bg: "#111111",
  },
  {
    icon: TrendingUp,
    title: "Collection value",
    body: "Track your rotation like a portfolio — live market value of your collection, price charts, and trends for All, Upcoming, Recently Dropped and Trending heat.",
    bg: "#35B6E9",
  },
];

const marqueeWords = [
  "NEVER MISS A DROP",
  "GRAILS ONLY",
  "COP OR DROP",
  "DEADSTOCK",
  "ON FEET",
  "RETRO HEAT",
  "LACE UP",
];

function Blobs({ variant = "hero" }) {
  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      aria-hidden="true"
    >
      {variant === "hero" ? (
        <>
          <div className="kickz-blob w-[480px] h-[480px] -top-24 -left-24 bg-[radial-gradient(circle,#ff3d2e_0%,transparent_65%)]" />
          <div className="kickz-blob kickz-blob-slow w-[560px] h-[560px] top-1/3 -right-32 bg-[radial-gradient(circle,#35B6E9_0%,transparent_65%)]" />
          <div className="kickz-blob kickz-blob-rev w-[420px] h-[420px] bottom-0 left-1/3 bg-[radial-gradient(circle,#ffb12e_0%,transparent_65%)]" />
        </>
      ) : (
        <>
          <div className="kickz-blob w-[420px] h-[420px] -top-20 left-1/4 bg-[radial-gradient(circle,#E8721C_0%,transparent_65%)]" />
          <div className="kickz-blob kickz-blob-rev w-[480px] h-[480px] bottom-0 -right-24 bg-[radial-gradient(circle,#35B6E9_0%,transparent_65%)]" />
          <div className="kickz-blob kickz-blob-slow w-[360px] h-[360px] bottom-10 -left-20 bg-[radial-gradient(circle,#ff3d2e_0%,transparent_65%)]" />
        </>
      )}
    </div>
  );
}

function KickzMarquee({ bg = "#111", color = "#E8721C" }) {
  return (
    <div
      className="py-4 border-y-4 border-[#111] overflow-hidden"
      style={{ background: bg }}
      data-testid="kickz-marquee"
    >
      <Marquee speed={60} gradient={false} autoFill>
        {marqueeWords.map((w) => (
          <span
            key={w}
            className="mx-6 font-extrabold text-xl md:text-2xl uppercase tracking-wide flex items-center gap-6"
            style={{ color }}
          >
            {w} <span className="text-white">★</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export default function CaseKickz() {
  return (
    <main
      className="min-h-screen bg-[#F7F2EA] text-[#111] font-kickz"
      data-testid="case-kickz-page"
    >
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#F7F2EA]/90 backdrop-blur-md border-b-4 border-[#111]">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="flex items-center gap-2 font-bold text-sm uppercase tracking-wide hover:text-[#E8721C] transition-colors"
            data-testid="kickz-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All work
          </Link>
          <img
            src={LOGO}
            alt="K!ckz logo"
            className="h-8 md:h-9 w-auto"
            data-testid="kickz-nav-logo"
          />
          <Link
            to="/work/sayso"
            className="hidden md:flex items-center gap-2 font-bold text-sm uppercase tracking-wide hover:text-[#E8721C] transition-colors"
            data-testid="kickz-next-link"
          >
            Next project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#E8721C] text-white overflow-hidden">
        <Blobs variant="hero" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 pt-20 pb-16 md:pt-28 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-wrap gap-3 mb-10">
              {["Mobile App", "E-commerce", "Sneaker Culture", "2026"].map(
                (t, i) => (
                  <span
                    key={t}
                    className="px-4 py-1.5 rounded-full bg-[#111] text-white text-xs font-bold uppercase tracking-wider hover:scale-110 hover:-rotate-3 transition-transform duration-300 cursor-default"
                    style={{ rotate: `${(i % 2 ? 1 : -1) * 1.5}deg` }}
                  >
                    {t}
                  </span>
                )
              )}
            </div>
            <motion.img
              initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
              animate={{ opacity: 1, scale: 1, rotate: -2 }}
              transition={{ duration: 0.9, delay: 0.15, type: "spring" }}
              whileHover={{ rotate: 2, scale: 1.03 }}
              src={LOGO}
              alt="K!ckz — sneaker drops app"
              className="w-[min(620px,80vw)] drop-shadow-[10px_12px_0_rgba(0,0,0,0.3)]"
              data-testid="kickz-hero-logo"
            />
            <p className="mt-8 text-2xl md:text-4xl font-bold max-w-[24ch]">
              Never miss a drop.
            </p>
          </motion.div>

          {/* Meta */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              ["Role", "UI/UX & Brand Design"],
              ["Year", "2026"],
              ["Platform", "iOS · Android"],
              ["Deliverables", "App design, logo, design system"],
            ].map(([k, v]) => (
              <div
                key={k}
                className="bg-white/15 rounded-2xl p-5 backdrop-blur-sm hover:bg-white/25 transition-colors duration-300"
              >
                <div className="text-xs uppercase tracking-widest font-bold opacity-80">
                  {k}
                </div>
                <div className="mt-1 font-bold text-sm md:text-base">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <KickzMarquee bg="#111" color="#E8721C" />

      {/* Mockup */}
      <section className="relative bg-[#E8721C] py-20 md:py-28 overflow-hidden">
        <Blobs variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <motion.img
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            src={MOCKUP}
            alt="K!ckz app screens"
            className="w-full rounded-[32px] border-4 border-[#111] shadow-[12px_12px_0_#111]"
            data-testid="kickz-mockup-image"
          />
        </div>
      </section>

      {/* Brief */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#35B6E9] text-white text-xs font-extrabold uppercase tracking-wider">
              The Brief
            </span>
          </div>
          <div className="md:col-span-8">
            <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">
              One app for the whole drop —{" "}
              <span className="text-[#E8721C]">browse, track, cop.</span>
            </h2>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#444]">
              Sneaker releases are chaotic: raffles on one site, restocks on
              another, release dates buried in blog posts. K!ckz pulls the
              entire drop culture into a single mobile experience — a shop, a
              release calendar and a hype radar in one pocket-sized package.
            </p>
          </div>
        </div>
      </section>

      {/* In Motion — video */}
      <section className="relative bg-[#111] text-white py-20 md:py-28 overflow-hidden">
        <Blobs variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1 flex justify-center">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -2 }}
              whileInView={{ opacity: 1, y: 0, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="w-[min(320px,80vw)] rounded-[44px] border-[10px] border-[#2a2a2a] bg-black shadow-[0_0_60px_rgba(232,114,28,0.35)] overflow-hidden"
            >
              <video
                src={VIDEO}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto block"
                data-testid="kickz-demo-video"
              />
            </motion.div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8721C] text-white text-xs font-extrabold uppercase tracking-wider">
              In Motion
            </span>
            <h2 className="mt-6 text-3xl md:text-5xl font-extrabold leading-tight">
              On feet, <span className="text-[#35B6E9]">on screen.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/70">
              A walk through the live prototype — from the splash screen to
              browsing the latest Jordan heat. Watch how the color-blocked
              tiles, sticky search and bottom tab bar keep the whole journey
              two taps deep, max.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-white/70">
              Every interaction is tuned for sneakerhead reflexes: wishlist
              hearts within thumb reach, drop reminders one toggle away.
            </p>
          </div>
        </div>
      </section>

      {/* Inspiration */}
      <section className="relative bg-[#F7F2EA] py-20 md:py-28 overflow-hidden">
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#111] text-white text-xs font-extrabold uppercase tracking-wider">
              Inspiration
            </span>
            <h2 className="mt-6 text-3xl md:text-5xl font-extrabold leading-tight">
              Sticker culture &{" "}
              <span className="text-[#E8721C]">release-day energy.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#444]">
              The identity borrows from sneaker sticker culture — chunky
              outlined letterforms that feel slapped onto a shoebox. The
              exclamation mark replacing the "i" captures the exact feeling of
              release day: the ping of a drop notification, the rush of copping
              before sell-out.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-[#444]">
              Behind the mark: soft airbrushed gradients of red, orange and
              blue — pulled from iconic Jordan colorways and sneaker-box dust —
              that bleed through the whole brand world.
            </p>
          </div>
          <div className="md:col-span-6">
            <motion.img
              whileHover={{ rotate: 1.5, scale: 1.02 }}
              src={GRADIENT_LOGO}
              alt="K!ckz logo on gradient exploration"
              className="w-full rounded-[32px] border-4 border-[#111] shadow-[12px_12px_0_#111]"
              data-testid="kickz-logo-image"
            />
          </div>
        </div>
      </section>

      {/* Marquee 2 */}
      <KickzMarquee bg="#35B6E9" color="#111" />

      {/* Inside the app — scrollable screens */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8721C] text-white text-xs font-extrabold uppercase tracking-wider">
            Inside the app
          </span>
          <h2 className="mt-6 text-3xl md:text-5xl font-extrabold leading-tight max-w-[22ch]">
            Scroll the screens like you own them.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
            {[
              {
                img: FEED_SCREEN,
                label: "Home feed — drops, fit checks & sneaker news",
                testid: "kickz-feed-screen",
              },
              {
                img: COLLECTION_SCREEN,
                label: "My Collection — live market value of your rotation",
                testid: "kickz-collection-screen",
              },
            ].map((s) => (
              <div key={s.testid}>
                <div className="rounded-[36px] border-4 border-[#111] shadow-[10px_10px_0_#111] bg-white max-h-[640px] overflow-y-auto kickz-scroll">
                  <img
                    src={s.img}
                    alt={s.label}
                    className="w-full"
                    data-testid={s.testid}
                  />
                </div>
                <p className="mt-4 text-center font-bold text-sm uppercase tracking-wide text-[#444]">
                  ↑ {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative bg-[#F7F2EA] py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#111] text-white text-xs font-extrabold uppercase tracking-wider">
            How it works
          </span>
          <h2 className="mt-6 text-3xl md:text-5xl font-extrabold leading-tight max-w-[20ch]">
            Built for the hunt, designed for the flex.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ rotate: i % 2 ? 1.2 : -1.2, scale: 1.02 }}
                className="rounded-[28px] border-4 border-[#111] p-8 shadow-[8px_8px_0_#111] bg-white"
                data-testid={`kickz-feature-${i}`}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: f.bg }}
                >
                  <f.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="mt-5 text-2xl font-extrabold">{f.title}</h3>
                <p className="mt-3 text-[#444] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design language */}
      <section className="relative bg-[#35B6E9] py-20 md:py-28 text-[#111] overflow-hidden">
        <Blobs variant="alt" />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white text-[#111] text-xs font-extrabold uppercase tracking-wider">
            Design Language
          </span>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="grid grid-cols-2 gap-4">
                {palette.map((c, i) => (
                  <motion.div
                    key={c.hex}
                    whileHover={{ rotate: i % 2 ? 2 : -2, scale: 1.04 }}
                    className="rounded-2xl border-4 border-[#111] p-6 h-32 flex flex-col justify-between"
                    style={{ background: c.hex, color: c.text }}
                    data-testid={`kickz-swatch-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <span className="font-extrabold">{c.name}</span>
                    <span className="font-mono text-sm opacity-80">
                      {c.hex}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="md:col-span-5 bg-white rounded-[28px] border-4 border-[#111] p-8 shadow-[8px_8px_0_#111]">
              <div className="text-[80px] font-extrabold leading-none">
                Aa<span className="text-[#E8721C]">!</span>
              </div>
              <div className="mt-4 font-extrabold text-xl">
                Rounded, heavy, loud.
              </div>
              <p className="mt-3 text-[#444] leading-relaxed">
                A chunky rounded display face carries the sticker attitude,
                while pill chips, toggles and bold card frames keep every
                interaction thumb-friendly and unmistakably on brand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer / next */}
      <footer className="bg-[#111] text-white py-16">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Link
            to="/#work"
            className="flex items-center gap-2 font-bold uppercase tracking-wide text-sm hover:text-[#E8721C] transition-colors"
            data-testid="kickz-footer-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all work
          </Link>
          <Link
            to="/work/sayso"
            className="group flex items-center gap-4"
            data-testid="kickz-footer-next"
          >
            <span className="text-2xl md:text-4xl font-extrabold group-hover:text-[#35B6E9] transition-colors">
              Next: SaySo
            </span>
            <ArrowRight className="w-8 h-8 transition-transform duration-300 group-hover:translate-x-2" />
          </Link>
        </div>
      </footer>
    </main>
  );
}
